/**
 * RogueSprite — fully articulated, autonomously animated character.
 *
 * The Rogue is built from separate body parts (hat, torso, arms, legs,
 * boots, spyglass) that animate independently. A state machine drives
 * behavior based on game state:
 *
 *   - Swagger: sneaky side-to-side patrol near the cannon
 *   - Excited: faster movement, jumping, occasional pirouette
 *   - Nervous: shaking, sweating, backing away from danger
 *   - Celebrating: victory pose, big jump, fist pump
 *   - Shooting: recoil kick-back
 */

import { Container, Graphics } from 'pixi.js';
import { CONFIG } from '../game/config.js';
import { drawGearGraphic } from './gear-graphic.js';

// ── Layout constants ──────────────────────────────────────────────────
const SCALE = 0.7;

// Body part sizes (in local unscaled coords)
const HAT_W = 36, HAT_H = 18;
const HEAD_W = 28, HEAD_H = 12;
const TORSO_W = 38, TORSO_H = 30;
const ARM_LEN = 18;
const LEG_LEN = 14;
const BOOT_W = 14, BOOT_H = 8;

// Joint positions relative to torso center
const SHOULDER_Y = -10;
const HIP_Y = 14;
const NECK_Y = -16;

// Colors — tuned to the commissioned portrait (steampunk clock-tower rogue):
// near-black wide-brim hat, glowing amber eyes in a void face, deep teal coat,
// red cape, dark leather gauntlets, brass lantern.
const C = {
  hat: 0x14100c, hatHi: 0x2a2016, hatSh: 0x050302,
  band: 0x7a1c18,
  void: 0x050302,
  eye: 0xffb830, eyeHi: 0xfff0a0,
  coat: 0x1a4a3a, coatHi: 0x286558, coatSh: 0x0c2a20,
  collar: 0xd8c8a4,
  shirt: 0xd8c8a4,
  strap: 0x7a1c18,
  belt: 0x5a3a20, beltHi: 0xa8803a,
  glove: 0x14100c, gloveSh: 0x050302,
  pants: 0x2a1f14,
  boot: 0x14100c, buckle: 0xa8803a,
  tool: 0xa8803a,
  lanternGlow: 0xfff0a0, lanternCore: 0xffffff,
  cape: 0x8a221a, capeHi: 0xaa2e20, capeSh: 0x4a0e08,
  sweat: 0x8ecfdf,
  outline: 0x050302,
};

type RogueState = 'swagger' | 'excited' | 'nervous' | 'celebrating' | 'shooting' | 'stealing';

/**
 * Theft choreography, in order:
 *   approach → pry (three heaves on the seated gear) → hoist (gear pops free,
 *   up over his head) → windup → fling (gear released mid-swing) → follow
 *   (holds the follow-through, watching it go) → return.
 */
type TheftPhase = 'approach' | 'pry' | 'hoist' | 'windup' | 'fling' | 'follow' | 'return';

// Where the Rogue plants himself relative to the gear: beside it, not on top
// of it. Standing over the gear hid it behind his hat and coat, so the player
// never saw what he was doing to it.
const STAND_DX = 29;
const STAND_DY = 7;
const PRY_HEAVES = 3;
const PRY_PULL_PX = 5;
const HOIST_MS = 220;
const WINDUP_MS = 260;
const FLING_MS = 150;
const FOLLOW_MS = 380;
/** Fraction of the fling swing at which the gear leaves his hands. */
const FLING_RELEASE_T = 0.5;
/** How far past the glove (local px) the carried gear's center sits. */
const HOLD_EXT = 18;
const ARM_FORWARD = -Math.PI / 2;
const ARM_OVERHEAD = -Math.PI;
const ARM_COCKED = -Math.PI - 0.75;
const ARM_FOLLOW = -1.1;

/** Hand-off payload for the moment the gear leaves the Rogue's hands. */
export interface GearReleaseInfo {
  readonly position: { x: number; y: number };
  readonly rotation: number;
  readonly color: number;
  /** -1 = thrown toward the left edge, +1 = toward the right. */
  readonly dir: number;
}

/** Renderer-supplied hooks for theft beats that need board-level VFX/SFX. */
export interface RogueTheftHooks {
  /** A heave slipped — the gear jolted in its seat. World-space position. */
  onPryJolt?(gearPos: { x: number; y: number }): void;
  /** The gear has left his hands; spawn the flight effect from here. */
  onGearRelease?(info: GearReleaseInfo): void;
}

export class RogueSprite {
  readonly container: Container;
  private built = false;
  private destroyed = false;

  // Body part containers (each rotates around its joint)
  private body!: Container;   // root that moves across the screen
  private torso!: Graphics;
  private headGroup!: Container;
  private hat!: Graphics;
  private faceVoid!: Graphics;
  private eyes!: Graphics;
  private smirk!: Graphics;
  private leftArm!: Container;
  private rightArm!: Container;
  private leftLeg!: Container;
  private rightLeg!: Container;
  private cape!: Graphics;
  private strapGfx!: Graphics;
  private satchelGfx!: Graphics;
  private sweatParticles: Graphics[] = [];

  // Animation state
  private time = 0;
  private state: RogueState = 'swagger';
  private stateTimer = 0;

  // Position (the Rogue moves around the screen!)
  private posX: number;
  private posY: number;
  private targetX: number;
  private targetY: number;
  private readonly restY: number;
  private velX = 0;
  private velY = 0;
  private facingRight = true;

  // Theft state — when set, overrides autonomous movement.
  private theftPhase: TheftPhase | null = null;
  private theftPhaseElapsed = 0;
  private theftPryMs = 800;
  /** Live world-space position of the target gear (it descends with the board). */
  private theftGearX = 0;
  private theftGearY = 0;
  /** +1 = Rogue stands left of the gear facing right; -1 = mirrored. */
  private theftSide = 1;
  private theftThrowDir = 1;
  private theftJoltsFired = 0;
  private theftReleased = false;
  /** 0-1 how hard he's hauling on the gear this frame. */
  private pryStrain = 0;
  private pryJitterX = 0;
  private pryJitterY = 0;
  /** Both arms share one angle during the hands-on phases. */
  private theftArmAngle = 0;
  /** Whole-body lean, in world sense (positive tips the hat toward +x). */
  private theftLean = 0;
  private heldGear!: Graphics;
  private heldGearColor = 0xffffff;
  private heldGearX = 0;
  private heldGearY = 0;
  private satchelBulge = 0;

  /** Set by the game renderer; see RogueTheftHooks. */
  theftHooks: RogueTheftHooks = {};

  // Approach/return interpolation — see updateTheftMovement().
  // Velocity-based movement can't cover the distance from the side gutter to
  // a gear on the far side of the board within the store's 1.4 s approach
  // budget, so we directly lerp posX/posY along an ease-out curve that lands
  // exactly on the target when the phase timer elapses.
  private theftLerpStartX = 0;
  private theftLerpStartY = 0;
  private theftLerpEndX = 0;
  private theftLerpEndY = 0;
  private theftLerpElapsed = 0;
  private theftLerpDuration = 0;

  // Walk cycle phase
  private walkPhase = 0;

  // Excitement (0-1, smoothly interpolated)
  private excitement = 0;

  // Danger level (0-1, from nearFailureCount)
  private danger = 0;

  // Action timers
  private jumpTimer = 0;
  private jumpVelY = 0;
  private jumpY = 0;
  private pirouetteAngle = 0;
  private pirouetting = false;
  private celebrateTimer = 0;
  private recoilTimer = 0;
  private wasFiring = false;

  // Dodge (dive out of the way when shot at)
  private dodgeTimer = 0;
  private dodgeDir = 0; // -1 = dive left, +1 = dive right

  // Auto-behavior cooldowns
  private nextActionTime = 2000;
  private sweatTimer = 0;

  // Scene bounds (in logical coords) within which the Rogue is free to
  // scamper. Defaults to the game-board rectangle; widened via
  // setSceneBounds() once the Pixi canvas is known (on wider viewports the
  // canvas extends past the board on each side).
  private sceneLeft: number = 0;
  private sceneRight: number = CONFIG.BOARD_WIDTH_PX;

  constructor() {
    this.container = new Container();
    this.posX = CONFIG.BOARD_WIDTH_PX / 2 - 30;
    this.posY = CONFIG.SHOOTER_Y + 5;
    this.restY = this.posY;
    this.targetX = this.posX;
    this.targetY = this.posY;
    this.buildBody();
  }

  /**
   * Set the horizontal bounds of the visible scene (in the same logical
   * coordinate space as posX). Called by the game renderer whenever the
   * canvas is resized. On desktop viewports the canvas is wider than the
   * 375-px game board, and without this update the Rogue's chooseAction
   * logic would keep him corralled along the board's edges instead of
   * letting him roam the newly-exposed margins.
   */
  setSceneBounds(left: number, right: number): void {
    this.sceneLeft = left;
    this.sceneRight = right;
  }

  // ── Build all body parts as Graphics ──────────────────────────────
  private buildBody(): void {
    this.body = new Container();
    this.body.scale.set(SCALE);
    this.body.position.set(this.posX, this.posY);
    this.container.addChild(this.body);

    // Carried gear — lives in world space (not inside the mirrored/scaled
    // body) and draws over the Rogue so the prize stays fully visible.
    this.heldGear = new Graphics();
    this.heldGear.visible = false;
    this.container.addChild(this.heldGear);

    // Legs (behind torso)
    this.leftLeg = this.createLeg();
    this.leftLeg.position.set(-6, HIP_Y);
    this.body.addChild(this.leftLeg);

    this.rightLeg = this.createLeg();
    this.rightLeg.position.set(6, HIP_Y);
    this.body.addChild(this.rightLeg);

    // Cape — sits behind the torso, visible flaring past the hips
    this.cape = this.createCape();
    this.body.addChild(this.cape);

    // Torso
    this.torso = this.createTorso();
    this.body.addChild(this.torso);

    // Satchel strap + bag (on torso)
    this.strapGfx = new Graphics();
    this.body.addChild(this.strapGfx);
    this.satchelGfx = new Graphics();
    this.body.addChild(this.satchelGfx);
    this.drawStrap();

    // Arms (in front of torso)
    this.leftArm = this.createArm(true);
    this.leftArm.position.set(-TORSO_W / 2 + 2, SHOULDER_Y);
    this.body.addChild(this.leftArm);

    this.rightArm = this.createArm(false);
    this.rightArm.position.set(TORSO_W / 2 - 2, SHOULDER_Y);
    this.body.addChild(this.rightArm);

    // Head group (on top)
    this.headGroup = new Container();
    this.headGroup.position.set(0, NECK_Y);
    this.body.addChild(this.headGroup);

    // Collar
    const collar = new Graphics();
    collar.rect(-HEAD_W / 2 - 2, -2, HEAD_W + 4, 8);
    collar.fill({ color: C.collar });
    collar.rect(-HEAD_W / 2 - 2, -2, HEAD_W + 4, 8);
    collar.stroke({ color: C.outline, width: 1.5 });
    this.headGroup.addChild(collar);

    // Face void — very tall and deep, maximum mystery
    const VOID_H = HEAD_H + 7;
    this.faceVoid = new Graphics();
    this.faceVoid.rect(-HEAD_W / 2, -VOID_H, HEAD_W, VOID_H);
    this.faceVoid.fill({ color: C.void });
    // Deep black under brim — the top half is pure darkness
    this.faceVoid.rect(-HEAD_W / 2, -VOID_H, HEAD_W, VOID_H * 0.45);
    this.faceVoid.fill({ color: 0x000000 });
    this.faceVoid.rect(-HEAD_W / 2, -VOID_H, HEAD_W, VOID_H);
    this.faceVoid.stroke({ color: C.outline, width: 1.5 });
    this.headGroup.addChild(this.faceVoid);

    // Eyes (redrawn each frame for expressiveness)
    this.eyes = new Graphics();
    this.headGroup.addChild(this.eyes);

    // Smirk
    this.smirk = new Graphics();
    this.headGroup.addChild(this.smirk);

    // Hat
    this.hat = this.createHat();
    this.hat.position.set(0, -HEAD_H);
    this.headGroup.addChild(this.hat);

    this.built = true;
  }

  private createHat(): Graphics {
    const g = new Graphics();
    // Very wide brim — massive shadow casting
    g.rect(-HAT_W / 2 - 11, 0, HAT_W + 22, 5);
    g.fill({ color: C.hat });
    // Thick brim underside shadow
    g.rect(-HAT_W / 2 - 11, 3, HAT_W + 22, 4);
    g.fill({ color: C.hatSh });
    // Low squat crown — barely visible above the brim
    g.ellipse(0, -3, HAT_W / 2, HAT_H - 11);
    g.fill({ color: C.hat });
    // Subtle highlight
    g.ellipse(-3, -5, HAT_W / 6, HAT_H / 6);
    g.fill({ color: C.hatHi });
    // Band — dark red leather wrap
    g.rect(-HAT_W / 2, 0, HAT_W, 2);
    g.fill({ color: C.band });
    // Outline
    g.ellipse(0, -3, HAT_W / 2 + 1, HAT_H - 10);
    g.stroke({ color: C.outline, width: 2 });
    g.rect(-HAT_W / 2 - 12, -1, HAT_W + 24, 8);
    g.stroke({ color: C.outline, width: 2 });
    return g;
  }

  private createTorso(): Graphics {
    const g = new Graphics();
    const hw = TORSO_W / 2, hh = TORSO_H / 2;
    // Coat body with puffy shoulders
    g.ellipse(-hw + 6, SHOULDER_Y + 6, 10, 8); g.fill({ color: C.coatHi }); // left shoulder
    g.ellipse(hw - 6, SHOULDER_Y + 6, 10, 8); g.fill({ color: C.coatHi });  // right shoulder
    g.roundRect(-hw, SHOULDER_Y, TORSO_W, TORSO_H + 4, 4);
    g.fill({ color: C.coat });
    // Shadow
    g.rect(-hw + 4, 4, TORSO_W - 8, hh);
    g.fill({ color: C.coatSh });
    // Center line
    g.rect(-0.5, SHOULDER_Y + 4, 1, TORSO_H - 2);
    g.fill({ color: C.outline });
    // Shirt V
    g.moveTo(-5, SHOULDER_Y + 5);
    g.lineTo(0, 8);
    g.lineTo(5, SHOULDER_Y + 5);
    g.closePath();
    g.fill({ color: C.shirt });
    g.moveTo(-5, SHOULDER_Y + 5); g.lineTo(0, 8); g.lineTo(5, SHOULDER_Y + 5);
    g.stroke({ color: C.outline, width: 1 });
    // Buttons
    for (const by of [-2, 3, 8]) {
      g.circle(0, by, 1.5);
      g.fill({ color: C.belt });
      g.circle(0, by, 1.5);
      g.stroke({ color: C.outline, width: 0.5 });
    }
    // Belt
    g.rect(-hw + 1, hh - 2, TORSO_W - 2, 4);
    g.fill({ color: C.belt });
    g.rect(-hw + 1, hh - 2, TORSO_W - 2, 4);
    g.stroke({ color: C.outline, width: 1 });
    g.rect(-4, hh - 2, 8, 4);
    g.fill({ color: C.beltHi });
    g.rect(-4, hh - 2, 8, 4);
    g.stroke({ color: C.outline, width: 0.8 });
    // Pockets
    g.rect(-hw + 2, 2, 7, 5); g.fill({ color: C.coatSh });
    g.rect(-hw + 2, 2, 7, 5); g.stroke({ color: C.outline, width: 0.8 });
    g.rect(hw - 9, 2, 7, 5); g.fill({ color: C.coatSh });
    g.rect(hw - 9, 2, 7, 5); g.stroke({ color: C.outline, width: 0.8 });
    // Outline
    g.roundRect(-hw - 0.5, SHOULDER_Y - 0.5, TORSO_W + 1, TORSO_H + 5, 4);
    g.stroke({ color: C.outline, width: 2 });
    return g;
  }

  private createArm(isLeft: boolean): Container {
    const arm = new Container();
    // Upper arm
    const upper = new Graphics();
    upper.roundRect(-3, 0, 6, ARM_LEN, 2);
    upper.fill({ color: C.coat });
    upper.roundRect(-3, 0, 6, ARM_LEN, 2);
    upper.stroke({ color: C.outline, width: 1.5 });
    // Cuff
    upper.rect(-4, ARM_LEN - 3, 8, 3);
    upper.fill({ color: C.coatHi });
    upper.rect(-4, ARM_LEN - 3, 8, 3);
    upper.stroke({ color: C.outline, width: 0.8 });
    arm.addChild(upper);
    // Glove
    const glove = new Graphics();
    glove.circle(0, ARM_LEN + 3, 4);
    glove.fill({ color: C.glove });
    glove.circle(0, ARM_LEN + 3, 4);
    glove.stroke({ color: C.outline, width: 1.5 });
    arm.addChild(glove);
    // Lantern (right arm only — brass frame with glowing amber core)
    if (!isLeft) {
      const lantern = new Graphics();
      const ly0 = ARM_LEN + 6;
      // Top hanger loop into glove
      lantern.rect(-0.5, ly0 - 2, 1, 2);
      lantern.fill({ color: C.tool });
      // Top cap
      lantern.rect(-3, ly0, 6, 1.5);
      lantern.fill({ color: C.tool });
      // Glowing glass body
      lantern.rect(-2.5, ly0 + 1.5, 5, 5);
      lantern.fill({ color: C.lanternGlow });
      // Side frame rails
      lantern.rect(-3, ly0 + 1.5, 1, 5);
      lantern.fill({ color: C.tool });
      lantern.rect(2, ly0 + 1.5, 1, 5);
      lantern.fill({ color: C.tool });
      // Hot white core
      lantern.rect(-1, ly0 + 3, 2, 2);
      lantern.fill({ color: C.lanternCore });
      // Bottom cap
      lantern.rect(-3, ly0 + 6.5, 6, 1.5);
      lantern.fill({ color: C.tool });
      // Outline around full silhouette
      lantern.rect(-3, ly0, 6, 8);
      lantern.stroke({ color: C.outline, width: 0.8 });
      arm.addChild(lantern);
    }
    return arm;
  }

  private createLeg(): Container {
    const leg = new Container();
    // Leg
    const upper = new Graphics();
    upper.roundRect(-3, 0, 6, LEG_LEN, 2);
    upper.fill({ color: C.pants });
    upper.roundRect(-3, 0, 6, LEG_LEN, 2);
    upper.stroke({ color: C.outline, width: 1.5 });
    leg.addChild(upper);
    // Boot
    const boot = new Graphics();
    boot.roundRect(-BOOT_W / 2, LEG_LEN, BOOT_W, BOOT_H, 2);
    boot.fill({ color: C.boot });
    boot.rect(-BOOT_W / 2 + 2, LEG_LEN + 1, 5, 3);
    boot.fill({ color: C.buckle });
    boot.rect(-BOOT_W / 2 + 2, LEG_LEN + 1, 5, 3);
    boot.stroke({ color: C.outline, width: 0.5 });
    boot.roundRect(-BOOT_W / 2, LEG_LEN, BOOT_W, BOOT_H, 2);
    boot.stroke({ color: C.outline, width: 1.5 });
    leg.addChild(boot);
    return leg;
  }

  private createCape(): Graphics {
    const g = new Graphics();
    // Trapezoid hanging from behind the shoulders: narrow at top (tucked
    // behind the torso silhouette), flaring wider past the hips so the red
    // edge is visible on either side of the legs.
    const top = SHOULDER_Y + 2;     // -8
    const bottom = HIP_Y + 14;      // 28 — past the belt, above the boots
    const topHalf = 6;
    const botHalf = 22;
    const notch = 3;                // shallow V at the hem
    g.moveTo(-topHalf, top);
    g.lineTo(topHalf, top);
    g.lineTo(botHalf, bottom);
    g.lineTo(botHalf - 4, bottom + 3);
    g.lineTo(0, bottom);
    g.lineTo(-botHalf + 4, bottom + 3);
    g.lineTo(-botHalf, bottom);
    g.closePath();
    g.fill({ color: C.cape });
    // Shadow fold down the left inner face
    g.moveTo(-2, top + 1);
    g.lineTo(-botHalf + 6, bottom - 1);
    g.lineTo(-botHalf + notch, bottom);
    g.lineTo(-1, top + 1);
    g.closePath();
    g.fill({ color: C.capeSh });
    // Highlight along the right outer face
    g.moveTo(topHalf - 1, top + 1);
    g.lineTo(botHalf - 2, bottom - 1);
    g.lineTo(botHalf - 5, bottom - 1);
    g.lineTo(topHalf - 3, top + 1);
    g.closePath();
    g.fill({ color: C.capeHi });
    // Outline
    g.moveTo(-topHalf, top);
    g.lineTo(topHalf, top);
    g.lineTo(botHalf, bottom);
    g.lineTo(botHalf - 4, bottom + 3);
    g.lineTo(0, bottom);
    g.lineTo(-botHalf + 4, bottom + 3);
    g.lineTo(-botHalf, bottom);
    g.closePath();
    g.stroke({ color: C.outline, width: 1.5 });
    return g;
  }

  private drawStrap(): void {
    const g = this.strapGfx;
    g.clear();
    g.moveTo(-12, SHOULDER_Y + 2);
    g.lineTo(14, HIP_Y - 2);
    g.stroke({ color: C.strap, width: 3 });
    g.moveTo(-13, SHOULDER_Y + 1);
    g.lineTo(13, HIP_Y - 3);
    g.stroke({ color: C.outline, width: 0.8 });
    g.moveTo(-11, SHOULDER_Y + 3);
    g.lineTo(15, HIP_Y - 1);
    g.stroke({ color: C.outline, width: 0.8 });
    // Satchel
    const sg = this.satchelGfx;
    sg.clear();
    sg.rect(12, HIP_Y - 4, 8, 7);
    sg.fill({ color: 0x5c3a1e });
    sg.rect(12, HIP_Y - 4, 8, 7);
    sg.stroke({ color: C.outline, width: 1.5 });
    sg.rect(14, HIP_Y - 2, 3, 3);
    sg.fill({ color: C.eye });
    sg.rect(15, HIP_Y - 1, 1, 1);
    sg.fill({ color: C.eyeHi });
  }

  // ── Redraw dynamic elements (eyes, smirk, sweat) ─────────────────
  private drawEyes(wink: boolean, wide: boolean, lookUp: boolean): void {
    const g = this.eyes;
    g.clear();
    // Tiny glowing pinpoints deep in the void
    const eyeY = -HEAD_H / 2 - 5 + (lookUp ? -2 : 0);
    const eyeH = wide ? 2 : 2;
    // Left eye — 2x2 amber dot
    g.rect(-7, eyeY, 2, eyeH);
    g.fill({ color: C.eye });
    g.rect(-7, eyeY, 1, 1);
    g.fill({ color: C.eyeHi });
    // Right eye (winking = 2x1 slit)
    const rh = wink ? 1 : eyeH;
    const ry = wink ? eyeY + 1 : eyeY;
    g.rect(5, ry, 2, rh);
    g.fill({ color: C.eye });
    g.rect(5, ry, 1, 1);
    g.fill({ color: C.eyeHi });
  }

  private drawSmirk(nervous: boolean): void {
    const g = this.smirk;
    g.clear();
    if (nervous) {
      // Worried squiggle
      g.rect(-2, -3, 6, 1);
      g.fill({ color: C.eye });
    } else {
      // Sly smirk
      g.rect(1, -3, 6, 1);
      g.fill({ color: C.eye });
      g.rect(7, -4, 2, 1);
      g.fill({ color: C.eye });
      g.rect(0, -3, 1, 1);
      g.fill({ color: 0xb8922e });
    }
  }

  private spawnSweat(): void {
    const drop = new Graphics();
    drop.ellipse(0, 0, 1.5, 2.5);
    drop.fill({ color: C.sweat });
    drop.position.set(
      (Math.random() - 0.5) * 20,
      NECK_Y - HEAD_H - 10,
    );
    this.body.addChild(drop);
    this.sweatParticles.push(drop);
  }

  // ── Main update ───────────────────────────────────────────────────
  /**
   * @param aimAngle - aim direction
   * @param isFiring - projectile in flight
   * @param dt - frame delta ms
   * @param multiplierX10 - streak multiplier (10 = 1x, 50 = 5x)
   * @param nearFailureCount - how many times a bubble landed near game over
   * @param lowestBubbleRow - how close gears are to the cannon (0 = top, higher = danger)
   * @param gameOverRow - the row at which game is over
   */
  update(
    aimAngle: number,
    isFiring: boolean,
    dt: number,
    multiplierX10: number = 10,
    nearFailureCount: number = 0,
    lowestBubbleRow: number = 0,
    gameOverRow: number = 20,
    theftFrozen: boolean = false,
  ): void {
    if (!this.built || this.destroyed) return;
    this.time += dt;
    // While the tower's descent is halted the heist holds mid-pose (no
    // release, no further gear-removal steps); the return walk is just
    // scampering, so it keeps going.
    const heistHeld = theftFrozen
      && this.theftPhase !== null && this.theftPhase !== 'return';

    // Smooth excitement (0-1 from multiplier)
    const targetExcite = Math.min(1, (multiplierX10 - 10) / 40);
    this.excitement += (targetExcite - this.excitement) * Math.min(1, dt * 0.003);

    // Danger level (how close gears are to cannon, 0-1)
    const proximity = gameOverRow > 0 ? lowestBubbleRow / gameOverRow : 0;
    const targetDanger = Math.max(0, proximity - 0.5) * 2; // 0 until halfway, then ramps to 1
    this.danger += (targetDanger - this.danger) * Math.min(1, dt * 0.004);

    // ── State machine ──────────────────────────────────────────────
    this.stateTimer -= dt;

    // Shooting recoil + dodge detection. Suppressed during a theft: the
    // steal is the Rogue's only gameplay-meaningful animation, and letting a
    // concurrent shot trigger a dodge (sideways lean + jump + altered
    // state) reads as "running off somewhere" instead of "running to the
    // gear," which is exactly the complaint we're fixing here.
    if (isFiring && !this.wasFiring && this.theftPhase === null) {
      this.recoilTimer = 250;
      this.state = 'shooting';

      // Check if the shot is heading toward the Rogue — if so, DODGE!
      const cannonX = CONFIG.BOARD_WIDTH_PX / 2;
      const shotDirX = Math.cos(aimAngle);
      // Shot goes left if shotDirX < 0, right if > 0
      const rogueIsLeft = this.posX < cannonX;
      const shotHeadedAtRogue =
        (rogueIsLeft && shotDirX < -0.3) || (!rogueIsLeft && shotDirX > 0.3);
      if (shotHeadedAtRogue) {
        this.dodgeTimer = 400;
        // Dive AWAY from the shot (opposite direction)
        this.dodgeDir = rogueIsLeft ? -1 : 1;
        this.jump(5); // jump while dodging
      }
    }
    this.wasFiring = isFiring;
    if (this.recoilTimer > 0) {
      this.recoilTimer = Math.max(0, this.recoilTimer - dt);
      if (this.recoilTimer === 0 && this.state === 'shooting') {
        this.state = 'swagger';
      }
    }

    // Victory celebration overrides
    if (this.celebrateTimer > 0) {
      this.celebrateTimer = Math.max(0, this.celebrateTimer - dt);
      this.state = 'celebrating';
      if (this.celebrateTimer === 0) this.state = 'swagger';
    }

    // Auto-state transitions — theft overrides all other autonomous states.
    if (this.theftPhase !== null) {
      this.state = 'stealing';
    } else if (this.state !== 'celebrating' && this.state !== 'shooting') {
      if (this.danger > 0.6) {
        this.state = 'nervous';
      } else if (this.excitement > 0.5) {
        this.state = 'excited';
      } else {
        this.state = 'swagger';
      }
    }

    // ── Auto-behavior (choose random actions) ──────────────────────
    // Skip while actively stealing — the theft phase drives movement.
    this.nextActionTime -= dt;
    if (this.nextActionTime <= 0 && this.theftPhase === null) {
      this.chooseAction();
    }

    // Clear theft state once the return lerp has finished.
    if (this.theftPhase === 'return' && this.theftLerpElapsed >= this.theftLerpDuration) {
      this.theftPhase = null;
      this.nextActionTime = 500; // let autonomy kick back in shortly
    }
    // Satchel bulge decays back to zero after a successful grab.
    if (this.satchelBulge > 0) {
      this.satchelBulge = Math.max(0, this.satchelBulge - dt * 0.0005);
    }
    this.satchelGfx.scale.set(1 + this.satchelBulge * 0.35);

    // ── Dodge tick ──────────────────────────────────────────────────
    if (this.dodgeTimer > 0) {
      this.dodgeTimer = Math.max(0, this.dodgeTimer - dt);
      // Rapid lateral movement during dodge
      this.velX += this.dodgeDir * 0.6 * (dt / 16);
    }

    // ── Movement (walk toward target) ──────────────────────────────
    // The approach and return phases of a theft use a time-parameterized
    // ease-out lerp. Velocity-based movement can't reliably cover the full
    // distance to a gear on the far side of the board within the 1.4 s
    // approach window — the gear would vanish mid-stride and the theft would
    // read as unexplained gesticulation.
    let moving: boolean;
    if (heistHeld) {
      moving = false;
    } else if (this.theftPhase === 'approach' || this.theftPhase === 'return') {
      this.theftLerpElapsed += dt;
      const tRaw = Math.min(1, this.theftLerpElapsed / this.theftLerpDuration);
      // Ease-out cubic: fast start, gentle arrival.
      const t = 1 - Math.pow(1 - tRaw, 3);
      const startX = this.theftLerpStartX;
      const startY = this.theftLerpStartY;
      this.posX = startX + (this.theftLerpEndX - startX) * t;
      this.posY = startY + (this.theftLerpEndY - startY) * t;
      const dirX = this.theftLerpEndX - startX;
      if (Math.abs(dirX) > 2) this.facingRight = dirX > 0;
      moving = tRaw < 1;
    } else if (this.theftPhase !== null) {
      // Hands-on phases: feet planted, pose fully scripted.
      moving = false;
      this.updateTheftPose(dt);
    } else {
      const dx = this.targetX - this.posX;
      const dy = this.targetY - this.posY;
      moving = Math.abs(dx) > 2 || Math.abs(dy) > 2 || this.dodgeTimer > 0;
      if (moving) {
        const speed = this.dodgeTimer > 0
          ? 0.3
          : this.state === 'nervous' ? 0.12
          : this.state === 'excited' ? 0.18
          : 0.08;
        if (this.dodgeTimer <= 0) this.velX += Math.sign(dx) * speed * (dt / 16);
        this.velX *= 0.92; // friction
        this.posX += this.velX * (dt / 16);
        if (Math.abs(dx) > 2) this.facingRight = dx > 0;

        if (Math.abs(dy) > 2) {
          this.velY += Math.sign(dy) * speed * 0.8 * (dt / 16);
        }
        this.velY *= 0.88;
        this.posY += this.velY * (dt / 16);
      } else {
        this.velX *= 0.85;
        this.velY *= 0.85;
        this.posX += this.velX * (dt / 16);
        this.posY += this.velY * (dt / 16);
      }
    }

    // Walk cycle (drives legs)
    if (moving) {
      // Stealing is a dead sprint — legs churn noticeably faster than the
      // swagger/excited cadence so the run reads as urgent.
      const walkSpeed = this.state === 'stealing' ? 0.016
        : this.state === 'excited' ? 0.012
        : 0.008;
      this.walkPhase += dt * walkSpeed;
    } else {
      // Subtle idle sway
      this.walkPhase += dt * 0.002;
    }

    // ── Jump physics ───────────────────────────────────────────────
    if (this.jumpTimer > 0) {
      this.jumpTimer -= dt;
      this.jumpVelY -= SCALE * 0.015 * (dt / 16); // gravity
      this.jumpY += this.jumpVelY * (dt / 16);
      if (this.jumpY >= 0) {
        this.jumpY = 0;
        this.jumpVelY = 0;
        this.jumpTimer = 0;
      }
    }

    // ── Pirouette ──────────────────────────────────────────────────
    if (this.pirouetting) {
      this.pirouetteAngle += dt * 0.015;
      if (this.pirouetteAngle >= Math.PI * 2) {
        this.pirouetteAngle = 0;
        this.pirouetting = false;
      }
    }

    // ── Sweat particles (nervous) ──────────────────────────────────
    if (this.state === 'nervous') {
      this.sweatTimer -= dt;
      if (this.sweatTimer <= 0) {
        this.spawnSweat();
        this.sweatTimer = 200 + Math.random() * 300;
      }
    }
    // Update sweat
    for (let i = this.sweatParticles.length - 1; i >= 0; i--) {
      const p = this.sweatParticles[i]!;
      p.position.y -= 0.8 * (dt / 16);
      p.position.x += (Math.random() - 0.5) * 0.5;
      p.alpha -= dt * 0.002;
      if (p.alpha <= 0) {
        this.body.removeChild(p);
        p.destroy();
        this.sweatParticles.splice(i, 1);
      }
    }

    // ── Apply transforms ───────────────────────────────────────────
    const faceSign = this.facingRight ? 1 : -1;

    // Body position + jump
    this.body.position.set(this.posX, this.posY + this.jumpY);
    this.body.scale.set(faceSign * SCALE, SCALE);

    // Pirouette (rotate whole body)
    this.body.rotation = this.pirouetting
      ? this.pirouetteAngle
      : this.isHandsOnTheft() ? this.theftLean : 0;

    // Torso lean (into movement, dodging, or shaking when nervous).
    // Because facingRight tracks movement direction, `sign(dx) * faceSign` is
    // always +1 while running, so the lean collapses to a constant -0.08.
    const moveAngle = moving ? -0.08 : 0;
    const dodgeLean = this.dodgeTimer > 0 ? this.dodgeDir * -0.3 : 0; // dramatic dodge lean
    const nervousShake = this.state === 'nervous'
      ? Math.sin(this.time * 0.03) * 0.06 * this.danger
      : 0;
    this.torso.rotation = moveAngle + dodgeLean + nervousShake;

    // Head bob + tilt
    const headBob = Math.sin(this.walkPhase * Math.PI * 2) * (moving ? 1.5 : 0.5);
    this.headGroup.position.y = NECK_Y + headBob;
    const headTilt = this.state === 'nervous'
      ? Math.sin(this.time * 0.025) * 0.1 // nervous glancing
      : Math.sin(this.walkPhase * Math.PI) * 0.04; // subtle tilt
    this.headGroup.rotation = headTilt;

    // Hat extra bob (lags slightly behind head for secondary motion)
    this.hat.rotation = headTilt * 0.5 + Math.sin(this.walkPhase * Math.PI * 2 + 0.5) * 0.03;

    // Cape flutter — swings with the walk cycle, more pronounced while moving
    // so the red hem actually reads as fabric trailing the sprint.
    const capeFlutter = Math.sin(this.walkPhase * Math.PI * 2 + 0.6);
    this.cape.rotation = capeFlutter * (moving ? 0.12 : 0.04);

    // Arms swing (opposite to legs in walk cycle)
    const armSwing = moving
      ? Math.sin(this.walkPhase * Math.PI * 2) * 0.6
      : Math.sin(this.walkPhase * Math.PI) * 0.1; // idle sway

    let leftArmAngle = -armSwing;
    let rightArmAngle = armSwing;

    if (this.isHandsOnTheft()) {
      // Both hands on the gear (or what's left of the swing).
      leftArmAngle = this.theftArmAngle;
      rightArmAngle = this.theftArmAngle;
    } else if (this.state === 'celebrating') {
      // Victory pose: arms up!
      leftArmAngle = -2.5 + Math.sin(this.time * 0.01) * 0.2;
      rightArmAngle = -2.5 + Math.sin(this.time * 0.01 + 1) * 0.2;
    } else if (this.recoilTimer > 0) {
      // Recoil: arms back
      const t = this.recoilTimer / 250;
      rightArmAngle = t * 0.8;
      leftArmAngle = t * -0.3;
    } else if (this.state === 'nervous') {
      // Nervous: arms close to body, fidgeting
      leftArmAngle = 0.3 + Math.sin(this.time * 0.02) * 0.15;
      rightArmAngle = -0.3 + Math.sin(this.time * 0.025) * 0.15;
    }

    this.leftArm.rotation = leftArmAngle;
    this.rightArm.rotation = rightArmAngle;

    // Legs walk cycle
    const legSwing = moving
      ? Math.sin(this.walkPhase * Math.PI * 2) * 0.5
      : Math.sin(this.walkPhase * Math.PI * 0.5) * 0.05; // barely perceptible idle

    if (this.isHandsOnTheft()) {
      // Braced wide stance — he's putting his back into it.
      this.leftLeg.rotation = 0.38;
      this.rightLeg.rotation = -0.38;
    } else {
      this.leftLeg.rotation = legSwing;
      this.rightLeg.rotation = -legSwing;
    }

    if (!heistHeld) this.updateHeldGear(dt);

    // Squash/stretch on landing
    if (this.jumpTimer <= 0 && this.jumpY === 0 && Math.abs(this.jumpVelY) < 0.01) {
      // Normal
    }

    // Eyes
    const wink = this.state === 'swagger' && Math.sin(this.walkPhase * 0.3) > 0.95;
    const wideEyes = this.state === 'nervous';
    const lookUp = this.state === 'nervous';
    this.drawEyes(wink, wideEyes, lookUp);

    // Smirk
    this.drawSmirk(this.state === 'nervous');
  }

  // ── Auto-behavior: choose random actions ──────────────────────────
  // Movement zones: the Rogue stays on the SIDES of the play area,
  // not in the center where gears are. Only crosses over during epic moments.
  private chooseAction(): void {
    // Left gutter stretches from the scene's left edge up to just past the
    // board's left wall; the right gutter is symmetrical. On wider viewports
    // the gutters expand to fill the new margin so the Rogue actually uses
    // the extra real estate instead of clumping at the board edges.
    const LEFT_MIN = this.sceneLeft;
    const LEFT_MAX = 45;
    const RIGHT_MIN = CONFIG.BOARD_WIDTH_PX - 45;
    const RIGHT_MAX = this.sceneRight;
    const onLeft = this.posX < CONFIG.BOARD_WIDTH_PX / 2;

    const pickSidePos = () => {
      if (onLeft) return LEFT_MIN + Math.random() * (LEFT_MAX - LEFT_MIN);
      return RIGHT_MIN + Math.random() * (RIGHT_MAX - RIGHT_MIN);
    };

    const switchSides = () => {
      if (onLeft) return RIGHT_MIN + Math.random() * (RIGHT_MAX - RIGHT_MIN);
      return LEFT_MIN + Math.random() * (LEFT_MAX - LEFT_MIN);
    };

    switch (this.state) {
      case 'swagger': {
        const r = Math.random();
        if (r < 0.3) {
          // Patrol along current side
          this.targetX = pickSidePos();
          this.nextActionTime = 2000 + Math.random() * 3000;
        } else if (r < 0.4) {
          // Switch sides (sneaks across behind the gears)
          this.targetX = switchSides();
          this.nextActionTime = 3000 + Math.random() * 2000;
        } else {
          // Chill on current side
          this.nextActionTime = 1000 + Math.random() * 2000;
        }
        break;
      }
      case 'excited': {
        const r = Math.random();
        if (r < 0.2) {
          // Dash to other side!
          this.targetX = switchSides();
          this.nextActionTime = 1500 + Math.random() * 1500;
        } else if (r < 0.4) {
          // Jump on current side!
          this.jump(4 + this.excitement * 4);
          this.nextActionTime = 800 + Math.random() * 1200;
        } else if (r < 0.5) {
          // Pirouette on current side!
          this.pirouetting = true;
          this.pirouetteAngle = 0;
          this.jump(3);
          this.nextActionTime = 1500 + Math.random() * 1500;
        } else if (r < 0.6 && this.excitement > 0.7) {
          // EPIC: dash through the CENTER (rare, only at high excitement)
          this.targetX = CONFIG.BOARD_WIDTH_PX / 2 + (Math.random() - 0.5) * 30;
          this.nextActionTime = 800; // short — get through quick
        } else {
          // Move along current side
          this.targetX = pickSidePos();
          this.nextActionTime = 1000 + Math.random() * 1500;
        }
        break;
      }
      case 'nervous': {
        // Pace frantically on current side, close to the edge
        this.targetX = pickSidePos();
        this.nextActionTime = 500 + Math.random() * 800;
        break;
      }
      case 'celebrating': {
        // Jump around on current side, occasionally dash through center
        if (Math.random() < 0.5) {
          this.jump(6 + Math.random() * 3);
        }
        if (Math.random() < 0.3) {
          this.targetX = switchSides(); // victory lap across the screen
        } else {
          this.targetX = pickSidePos();
        }
        this.nextActionTime = 600 + Math.random() * 800;
        break;
      }
      default:
        this.nextActionTime = 1000;
    }
  }

  private jump(power: number): void {
    if (this.jumpY < 0) return; // already in air
    this.jumpTimer = 1000;
    this.jumpVelY = -power;
  }

  /**
   * Begin a gear theft — the Rogue breaks off autonomous patrol and runs to
   * the target gear. `targetPos` is in world-space pixels (post-board-offset),
   * matching the coordinate space of `posX`/`posY`.
   *
   * `approachMs` should match the store's `THEFT_APPROACH_MS` so the Rogue
   * arrives exactly as the grab event fires. Falls back to 1400ms if omitted.
   */
  startTheft(targetPos: { x: number; y: number }, approachMs: number = 1400): void {
    if (!this.built) return;
    // Cancel every ambient animation that could interrupt or visually
    // obscure the run. The steal is the Rogue's only gameplay-meaningful
    // animation, so the approach must read unambiguously — pirouette would
    // spin the body mid-sprint, a lingering celebrate/recoil timer would
    // override the state machine, dodge adds a sideways lean + velocity
    // burst, and a mid-air jump would plant the Rogue above the gear
    // instead of next to it. Clear them all before committing.
    this.pirouetting = false;
    this.pirouetteAngle = 0;
    this.celebrateTimer = 0;
    this.dodgeTimer = 0;
    this.recoilTimer = 0;
    this.jumpTimer = 0;
    this.jumpVelY = 0;
    this.jumpY = 0;

    this.theftPhase = 'approach';
    this.theftPhaseElapsed = 0;
    this.theftJoltsFired = 0;
    this.theftReleased = false;
    this.pryStrain = 0;
    this.theftLean = 0;
    this.heldGear.visible = false;
    this.theftGearX = targetPos.x;
    this.theftGearY = targetPos.y;
    // Plant beside the gear on whichever side he's coming from, unless that
    // spot is off the visible scene (edge gear on a narrow viewport).
    this.theftSide = this.posX <= targetPos.x ? 1 : -1;
    const standX = targetPos.x - this.theftSide * STAND_DX;
    if (standX < this.sceneLeft + 10) this.theftSide = -1;
    else if (standX > this.sceneRight - 10) this.theftSide = 1;
    const endX = targetPos.x - this.theftSide * STAND_DX;
    const endY = targetPos.y + STAND_DY;
    this.theftLerpStartX = this.posX;
    this.theftLerpStartY = this.posY;
    this.theftLerpEndX = endX;
    this.theftLerpEndY = endY;
    this.theftLerpElapsed = 0;
    this.theftLerpDuration = Math.max(1, approachMs);
    this.targetX = endX;
    this.targetY = endY;
    this.velX = 0;
    this.velY = 0;
    this.nextActionTime = 10_000; // park autonomous actions during theft
  }

  /**
   * Keep the theft locked onto the gear's live position. The board descends
   * continuously (fast, on a sparse board), so a target captured once at
   * theft start can be most of a row stale by the time he arrives — which
   * read as the Rogue grabbing at thin air while some other gear vanished.
   * Called every frame by the renderer while a theft is in flight.
   */
  setTheftTarget(gearPos: { x: number; y: number }): void {
    if (this.theftPhase !== 'approach' && this.theftPhase !== 'pry') return;
    this.theftGearX = gearPos.x;
    this.theftGearY = gearPos.y;
    if (this.theftPhase === 'approach') {
      this.theftLerpEndX = gearPos.x - this.theftSide * STAND_DX;
      this.theftLerpEndY = gearPos.y + STAND_DY;
    }
  }

  /**
   * Rogue has arrived — grip the gear and start hauling. `pryMs` should match
   * the store's `THEFT_GRAB_MS` so the final heave lands as the gear comes free.
   */
  beginGrab(pryMs: number = 800): void {
    if (!this.built || this.theftPhase !== 'approach') return;
    this.theftPhase = 'pry';
    this.theftPhaseElapsed = 0;
    this.theftPryMs = Math.max(1, pryMs);
    this.theftJoltsFired = 0;
    this.facingRight = this.theftSide > 0;
    this.velX = 0;
    this.velY = 0;
  }

  /**
   * The gear is free (the store has removed it from the board) — hoist it
   * overhead and hurl it off-screen. The flight itself is spawned through
   * `theftHooks.onGearRelease` at the moment it leaves his hands.
   */
  throwGear(color: number): void {
    if (!this.built) return;
    if (this.theftPhase !== 'pry') {
      // Driven out of sequence (animation playground) — throw from where he stands.
      this.theftGearX = this.posX + (this.facingRight ? 1 : -1) * STAND_DX;
      this.theftGearY = this.posY - STAND_DY;
      this.theftSide = this.facingRight ? 1 : -1;
    }
    const jiggle = this.getPryJiggle();
    this.heldGearX = this.theftGearX + (jiggle?.x ?? 0);
    this.heldGearY = this.theftGearY + (jiggle?.y ?? 0);
    this.heldGearColor = color;
    drawGearGraphic(this.heldGear, CONFIG.CELL_SIZE, color);
    this.heldGear.rotation = jiggle?.rotation ?? 0;
    this.heldGear.position.set(this.heldGearX, this.heldGearY);
    this.heldGear.visible = true;
    this.theftReleased = false;
    // Hurl it toward the farther scene edge: the longest possible flight, so
    // it crosses the board and carries on out through the far margin.
    const roomLeft = this.theftGearX - this.sceneLeft;
    const roomRight = this.sceneRight - this.theftGearX;
    this.theftThrowDir = roomLeft > roomRight ? -1 : 1;
    this.theftPhase = 'hoist';
    this.theftPhaseElapsed = 0;
    this.nextActionTime = 10_000;
  }

  /** Board-gear shake for the renderer to apply while he's hauling on it. */
  getPryJiggle(): { x: number; y: number; rotation: number } | null {
    if (this.theftPhase !== 'pry') return null;
    return {
      x: -this.theftSide * this.pryStrain * PRY_PULL_PX + this.pryJitterX,
      y: this.pryJitterY,
      rotation: Math.sin(this.time * 0.07) * 0.16 * this.pryStrain,
    };
  }

  /** True while a theft owns the Rogue (renderer uses this to keep tracking). */
  isStealing(): boolean {
    return this.theftPhase !== null;
  }

  private isHandsOnTheft(): boolean {
    return this.theftPhase !== null
      && this.theftPhase !== 'approach'
      && this.theftPhase !== 'return';
  }

  /** Scripted pose + position for every theft phase between run-in and run-out. */
  private updateTheftPose(dt: number): void {
    this.theftPhaseElapsed += dt;
    const side = this.theftSide;
    const dir = this.theftThrowDir;
    const standX = this.theftGearX - side * STAND_DX;
    const standY = this.theftGearY + STAND_DY;

    if (this.theftPhase === 'pry') {
      const t = Math.min(1, this.theftPhaseElapsed / this.theftPryMs);
      const heave = Math.min(PRY_HEAVES - 1, Math.floor(t * PRY_HEAVES));
      const p = t * PRY_HEAVES - heave;
      const last = heave === PRY_HEAVES - 1;
      // Each heave strains (ease-out), then slips back. The last one doesn't
      // slip — it holds at full strain until the gear lets go.
      const pull = Math.min(1, p / 0.7);
      let strain = 1 - (1 - pull) * (1 - pull);
      if (!last && p > 0.7) strain = 1 - (p - 0.7) / 0.3;
      this.pryStrain = strain * (0.55 + 0.45 * (heave / (PRY_HEAVES - 1)));

      const slips = heave + (!last && p > 0.7 ? 1 : 0);
      if (slips > this.theftJoltsFired) {
        this.theftJoltsFired = slips;
        this.theftHooks.onPryJolt?.({ x: this.theftGearX, y: this.theftGearY });
      }

      this.pryJitterX = (Math.random() - 0.5) * 1.8 * this.pryStrain;
      this.pryJitterY = (Math.random() - 0.5) * 1.8 * this.pryStrain;
      this.facingRight = side > 0;
      this.posX = standX - side * this.pryStrain * PRY_PULL_PX;
      this.posY = standY;
      this.theftLean = -side * this.pryStrain * 0.22;
      // Counter-rotate the arms so his hands stay level on the gear as he leans.
      this.theftArmAngle = ARM_FORWARD + this.pryStrain * 0.22;

      // The store never resolved the grab (e.g. run ended mid-pry) — let go.
      if (this.theftPhaseElapsed > this.theftPryMs + 600) this.finishTheft(false);
      return;
    }

    this.pryStrain = 0;
    this.posY = standY;

    if (this.theftPhase === 'hoist') {
      const t = Math.min(1, this.theftPhaseElapsed / HOIST_MS);
      const e = 1 - Math.pow(1 - t, 3);
      this.posX = standX - side * PRY_PULL_PX * (1 - e);
      // The gear giving way sends him up onto his toes.
      this.posY = standY - 5 * Math.sin(Math.PI * t);
      this.theftLean = -side * 0.22 * (1 - e);
      this.theftArmAngle = ARM_FORWARD + (ARM_OVERHEAD - ARM_FORWARD) * e;
      if (t >= 1) {
        // Arms are vertical, so the gear sits on his centerline and turning
        // to face the throw doesn't swing it.
        this.facingRight = dir > 0;
        this.theftPhase = 'windup';
        this.theftPhaseElapsed = 0;
      }
      return;
    }

    this.posX = standX;
    this.facingRight = dir > 0;

    if (this.theftPhase === 'windup') {
      const t = Math.min(1, this.theftPhaseElapsed / WINDUP_MS);
      const e = 1 - (1 - t) * (1 - t);
      this.theftArmAngle = ARM_OVERHEAD + (ARM_COCKED - ARM_OVERHEAD) * e;
      this.theftLean = -dir * 0.28 * e;
      this.posY = standY + 3 * e;
      if (t >= 1) {
        this.theftPhase = 'fling';
        this.theftPhaseElapsed = 0;
      }
      return;
    }

    if (this.theftPhase === 'fling') {
      const t = Math.min(1, this.theftPhaseElapsed / FLING_MS);
      const e = t * t;
      this.theftArmAngle = ARM_COCKED + (ARM_FOLLOW - ARM_COCKED) * e;
      this.theftLean = dir * (-0.28 + 0.6 * e);
      this.posY = standY + 3 * (1 - e);
      if (t >= 1) {
        this.theftPhase = 'follow';
        this.theftPhaseElapsed = 0;
      }
      return;
    }

    // follow — hold the follow-through and watch it go.
    const t = Math.min(1, this.theftPhaseElapsed / FOLLOW_MS);
    this.theftArmAngle = ARM_FOLLOW + 0.4 * t;
    this.theftLean = dir * 0.32 * (1 - t * 0.6);
    if (t >= 1) this.finishTheft(false);
  }

  /** Body-local point → world space (accounts for facing, scale, lean, hop). */
  private bodyToWorld(lx: number, ly: number): { x: number; y: number } {
    const sx = (this.facingRight ? 1 : -1) * SCALE;
    const r = this.body.rotation;
    const cos = Math.cos(r);
    const sin = Math.sin(r);
    return {
      x: this.posX + cos * sx * lx - sin * SCALE * ly,
      y: this.posY + this.jumpY + sin * sx * lx + cos * SCALE * ly,
    };
  }

  /** Carry the freed gear in his hands; release it mid-fling. */
  private updateHeldGear(dt: number): void {
    if (!this.heldGear.visible) return;
    const carrying = this.theftPhase === 'hoist'
      || this.theftPhase === 'windup'
      || this.theftPhase === 'fling'
      // A long frame can step clean over the fling; still release below.
      || this.theftPhase === 'follow';
    if (!carrying) {
      // Theft was cut short with the gear still in hand — don't strand it.
      this.heldGear.visible = false;
      return;
    }

    const reach = ARM_LEN + 3 + HOLD_EXT;
    const a = this.theftArmAngle;
    const grip = this.bodyToWorld(-Math.sin(a) * reach, SHOULDER_Y + Math.cos(a) * reach);
    // Ease onto the hands during the hoist (it starts in its board slot);
    // after that it's locked to them so the swing stays crisp.
    const follow = this.theftPhase === 'hoist' ? Math.min(1, dt * 0.03) : 1;
    this.heldGearX += (grip.x - this.heldGearX) * follow;
    this.heldGearY += (grip.y - this.heldGearY) * follow;
    this.heldGear.position.set(this.heldGearX, this.heldGearY);

    const swingDone = this.theftPhase === 'follow'
      || (this.theftPhase === 'fling'
        && this.theftPhaseElapsed / FLING_MS >= Math.sqrt(FLING_RELEASE_T));
    if (swingDone && !this.theftReleased) {
      this.theftReleased = true;
      this.heldGear.visible = false;
      this.theftHooks.onGearRelease?.({
        position: { x: this.heldGearX, y: this.heldGearY },
        rotation: this.heldGear.rotation,
        color: this.heldGearColor,
        dir: this.theftThrowDir,
      });
    }
  }

  /**
   * Grab resolved — return to the sidelines. When `stashed` is true, the gear
   * went into the satchel (bulges briefly). When false, the target was gone
   * before the Rogue could grab it — the return is empty-handed.
   */
  finishTheft(stashed: boolean): void {
    if (!this.built) return;
    this.theftPhase = 'return';
    if (stashed) this.satchelBulge = 1;
    // Pick a return target on the nearest side gutter.
    const onLeft = this.posX < CONFIG.BOARD_WIDTH_PX / 2;
    const LEFT_MIN = this.sceneLeft;
    const LEFT_MAX = 45;
    const RIGHT_MIN = CONFIG.BOARD_WIDTH_PX - 45;
    const RIGHT_MAX = this.sceneRight;
    const endX = onLeft
      ? LEFT_MIN + Math.random() * (LEFT_MAX - LEFT_MIN)
      : RIGHT_MIN + Math.random() * (RIGHT_MAX - RIGHT_MIN);
    this.theftLerpStartX = this.posX;
    this.theftLerpStartY = this.posY;
    this.theftLerpEndX = endX;
    this.theftLerpEndY = this.restY;
    this.theftLerpElapsed = 0;
    // Return is a bit snappier than the approach — the Rogue has what he came
    // for and scampers off.
    this.theftLerpDuration = 1100;
    this.targetX = endX;
    this.targetY = this.restY;
    this.velX = 0;
    this.velY = 0;
    this.nextActionTime = 10_000; // don't resume autonomy until the lerp is done
  }

  /** Trigger pop celebration. */
  reactToPop(popCount: number): void {
    if (!this.built || popCount < 3) return;
    // Mid-heist he's busy — a celebratory hop/pirouette would wrench him off
    // the gear and scramble the one animation the player needs to read.
    if (this.theftPhase !== null) return;
    const intensity = Math.min(1, popCount / 10);
    this.jump(3 + intensity * 5);
    if (intensity > 0.5 && Math.random() < 0.4) {
      this.pirouetting = true;
      this.pirouetteAngle = 0;
    }
  }

  /** Trigger victory celebration. */
  celebrate(): void {
    if (!this.built) return;
    this.celebrateTimer = 2500;
    this.jump(8);
    this.pirouetting = true;
    this.pirouetteAngle = 0;
  }

  destroy(): void {
    this.destroyed = true;
    for (const p of this.sweatParticles) p.destroy();
    this.sweatParticles = [];
    this.container.destroy({ children: true });
  }
}
